/**
 * The Technical Support slides a review adds itself — see migration 202.
 *
 * The template's two Technical Support slides are live from Autotask. These
 * are blank ones, added on the review form and filled in there: two
 * screenshots side by side, a description under each, and up to five
 * takeaways down the right, the same shape the rest of the deck uses.
 *
 * Held on the review as a JSON array, one object per slide in the order they
 * were added. The screenshots are ordinary slide images (138) in the slots
 * `SUPPORT_SLIDE_SLOTS[index]`.
 */
import { TAKEAWAY_SLOTS, emptyTakeaways } from './supportTakeaways';

/** How many a review may add. Four is two spreads of the deck's own slides —
 *  past that it is a deck of screenshots, not a review. */
export const MAX_SUPPORT_SLIDES = 4;

/** Screenshots on each added slide, side by side. */
export const SHOTS_PER_SUPPORT_SLIDE = 2;

export interface SupportSlide {
  /** Which pair of screenshot slots this slide owns, 0-based — see
   *  `SUPPORT_SLIDE_SLOTS`. Held on the slide rather than taken from its
   *  place in the list so that removing the first of three slides does not
   *  hand its screenshots to the one behind it. */
  pair: number;
  /** Shown under "Technical Support", so two added slides are told apart.
   *  Empty is fine — the slide then carries the heading alone. */
  heading: string;
  /** The description under each screenshot, one per shot. */
  notes: string[];
  /** Up to five, down the right. */
  takeaways: string[];
}

export const emptySupportSlide = (pair: number): SupportSlide => ({
  pair,
  heading: '',
  notes: Array<string>(SHOTS_PER_SUPPORT_SLIDE).fill(''),
  takeaways: emptyTakeaways(),
});

/** The first pair of slots no slide is using, or null when all four are. */
export function freeSupportPair(slides: SupportSlide[]): number | null {
  const taken = new Set(slides.map((s) => s.pair));
  for (let i = 0; i < MAX_SUPPORT_SLIDES; i++) if (!taken.has(i)) return i;
  return null;
}

/** Never throws: a malformed value reads as no added slides. Each slide is
 *  padded out to its full set of boxes, which is what the form edits. A
 *  slide saved without a usable pair — or with one another slide already
 *  has — takes the first free one, so the four pairs are never shared. */
export function parseSupportSlides(raw: string | null | undefined): SupportSlide[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const out: SupportSlide[] = [];
  for (const row of parsed.slice(0, MAX_SUPPORT_SLIDES)) {
    const r = (row ?? {}) as { pair?: unknown; heading?: unknown; notes?: unknown; takeaways?: unknown };
    const taken = new Set(out.map((s) => s.pair));
    const wanted =
      typeof r.pair === 'number' && Number.isInteger(r.pair) && r.pair >= 0 && r.pair < MAX_SUPPORT_SLIDES && !taken.has(r.pair)
        ? r.pair
        : freeSupportPair(out);
    if (wanted === null) break;
    const slide = emptySupportSlide(wanted);
    if (typeof r.heading === 'string') slide.heading = r.heading;
    if (Array.isArray(r.notes)) {
      r.notes.slice(0, SHOTS_PER_SUPPORT_SLIDE).forEach((n, i) => {
        if (typeof n === 'string') slide.notes[i] = n;
      });
    }
    if (Array.isArray(r.takeaways)) {
      r.takeaways.slice(0, TAKEAWAY_SLOTS).forEach((t, i) => {
        if (typeof t === 'string') slide.takeaways[i] = t;
      });
    }
    out.push(slide);
  }
  return out;
}

/** What a slide shows: trimmed, with the empty boxes dropped. A slide with
 *  nothing in it still shows — it was added on purpose, and a blank slide is
 *  something to talk over rather than a mistake. */
export function supportSlideView(slide: SupportSlide) {
  return {
    heading: slide.heading.trim(),
    notes: slide.notes.map((n) => n.trim()),
    takeaways: slide.takeaways.map((t) => t.trim()).filter(Boolean),
  };
}
